/**
 * Three.js visual effects for the onboarding sequence.
 * Floating status icons that follow workers (search, phone, checkmark).
 */

import * as THREE from 'three';
import type { WorkerManager } from '@/scenes/interior/worker-manager';

export type StatusIconType = 'search' | 'phone' | 'check' | 'document' | 'analyze' | 'error' | 'thinking' | 'pending';

export class OnboardingEffects {
  private scene: THREE.Scene;
  private workerManager: WorkerManager | null = null;
  private statusIcons: Map<number, THREE.Sprite> = new Map();
  private elapsed = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** Set the worker manager so icons can track worker positions */
  setWorkerManager(wm: WorkerManager): void {
    this.workerManager = wm;
  }

  /**
   * Show a floating status icon sprite above a worker's head.
   * - 'search' = magnifying glass on blue circle
   * - 'phone'  = phone on green circle
   * - 'check'  = checkmark on green circle
   */
  showStatusIcon(deskIndex: number, type: StatusIconType): void {
    // Remove existing icon for this desk
    this.hideStatusIcon(deskIndex);

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    if (type === 'search') {
      // Blue circle with magnifying glass
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Magnifying glass
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(26, 26, 12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(35, 35);
      ctx.lineTo(48, 48);
      ctx.stroke();
    } else if (type === 'phone') {
      // Green circle with phone
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Phone icon (simplified handset)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(20, 18);
      ctx.quadraticCurveTo(18, 28, 22, 34);
      ctx.quadraticCurveTo(28, 42, 36, 46);
      ctx.quadraticCurveTo(42, 48, 46, 42);
      ctx.stroke();

      // Earpiece and mouthpiece dots
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(20, 18, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(46, 42, 3, 0, Math.PI * 2);
      ctx.fill();
    } else if (type === 'check') {
      // Green circle with checkmark
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Checkmark
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(18, 32);
      ctx.lineTo(28, 42);
      ctx.lineTo(46, 22);
      ctx.stroke();
    } else if (type === 'document') {
      // Blue circle with page icon (for draft tasks)
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Page outline
      ctx.strokeStyle = '#ffffff';
      ctx.fillStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(20, 14);
      ctx.lineTo(36, 14);
      ctx.lineTo(44, 22);
      ctx.lineTo(44, 50);
      ctx.lineTo(20, 50);
      ctx.closePath();
      ctx.stroke();

      // Lines on page
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(25, 28); ctx.lineTo(39, 28);
      ctx.moveTo(25, 34); ctx.lineTo(39, 34);
      ctx.moveTo(25, 40); ctx.lineTo(33, 40);
      ctx.stroke();
    } else if (type === 'analyze') {
      // Purple circle with chart icon (for analysis)
      ctx.fillStyle = '#8b5cf6';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Bar chart
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(16, 36, 8, 14);
      ctx.fillRect(27, 26, 8, 24);
      ctx.fillRect(38, 18, 8, 32);
    } else if (type === 'error') {
      // Red circle with X
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(20, 20); ctx.lineTo(44, 44);
      ctx.moveTo(44, 20); ctx.lineTo(20, 44);
      ctx.stroke();
    } else if (type === 'thinking') {
      // Amber circle with thought bubble (three dots)
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Three dots (ellipsis — thinking indicator)
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(20, 32, 4, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(32, 32, 4, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(44, 32, 4, 0, Math.PI * 2); ctx.fill();
    } else if (type === 'pending') {
      // Grey circle with clock icon
      ctx.fillStyle = '#6b7280';
      ctx.beginPath();
      ctx.arc(32, 32, 28, 0, Math.PI * 2);
      ctx.fill();

      // Clock outline
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(32, 32, 16, 0, Math.PI * 2);
      ctx.stroke();

      // Clock hands
      ctx.lineCap = 'round';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(32, 32); ctx.lineTo(32, 20); // hour hand (up)
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(32, 32); ctx.lineTo(42, 32); // minute hand (right)
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.35, 0.35, 1);

    // Initial position — will be updated every frame to follow worker
    sprite.position.set(0, 2.5, 0);
    sprite.name = `status-icon-${deskIndex}`;
    this.scene.add(sprite);
    this.statusIcons.set(deskIndex, sprite);
  }

  hideStatusIcon(deskIndex: number): void {
    const sprite = this.statusIcons.get(deskIndex);
    if (sprite) {
      this.scene.remove(sprite);
      (sprite.material as THREE.SpriteMaterial).map?.dispose();
      sprite.material.dispose();
      this.statusIcons.delete(deskIndex);
    }
  }

  update(delta: number): void {
    this.elapsed += delta;

    // Follow worker positions every frame
    for (const [deskIndex, sprite] of this.statusIcons) {
      const worker = this.workerManager?.getWorker(deskIndex);
      if (worker) {
        // Follow the worker's actual world position
        const wp = worker.group.position;
        sprite.position.x = wp.x;
        sprite.position.y = wp.y + 2.2 + Math.sin(this.elapsed * 2.5) * 0.04;
        sprite.position.z = wp.z;
      }
    }
  }

  dispose(): void {
    for (const [idx] of this.statusIcons) this.hideStatusIcon(idx);
    this.statusIcons.clear();
  }
}
